import { ActionContext } from "vuex";
import garageService from "@/services/garageService";
import {
  GarageListParams,
  GarageListItemDto,
  GarageResponseDto,
  CreateGarageDto,
  UpdateGarageDto,
} from "@/models/garage.model";

interface State {
  garages: GarageListItemDto[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  currentGarage: GarageResponseDto | null;
}

type Ctx = ActionContext<State, any>;

const state: State = {
  garages: [],
  total: 0,
  page: 1,
  pageSize: 10,
  totalPages: 0,
  currentGarage: null,
};

const mutations = {
  setGarages(
    state: State,
    payload: {
      items: GarageListItemDto[];
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
    }
  ) {
    state.garages = payload.items;
    state.total = payload.total;
    state.page = payload.page;
    state.pageSize = payload.pageSize;
    state.totalPages = payload.totalPages;
  },
  setCurrentGarage(state: State, payload: GarageResponseDto | null) {
    state.currentGarage = payload;
  },
};

const actions = {
  async loadGarages({ commit, dispatch }: Ctx, params: GarageListParams = {}) {
    try {
      const response = await garageService.listGarages(params);
      commit("setGarages", response);
      return response;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async fetchGarage({ commit, dispatch }: Ctx, garageId: string) {
    try {
      const garage = await garageService.getGarageById(garageId);
      commit("setCurrentGarage", garage);
      return garage;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      commit("setCurrentGarage", null);
      throw error;
    }
  },

  async createGarage({ dispatch }: Ctx, data: CreateGarageDto) {
    try {
      const garage = await garageService.createGarage(data);
      dispatch("notificator/success", "garage-created-success", { root: true });
      return garage;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async updateGarage(
    { dispatch }: Ctx,
    payload: { garageId: string; data: UpdateGarageDto }
  ) {
    try {
      const garage = await garageService.updateGarage(payload.garageId, payload.data);
      dispatch("notificator/success", "garage-updated-success", { root: true });
      return garage;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async deleteGarage({ dispatch }: Ctx, garageId: string) {
    try {
      await garageService.deleteGarage(garageId);
      dispatch("notificator/success", "garage-deleted-success", { root: true });
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  }
};

const getters = {
  garageOptions(state: State) {
    return state.garages.map((g) => ({
      title: g.name,
      value: g.garageId,
    }));
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
